import { useEffect, useRef, type ElementRef } from 'react';
import { MotifHeader } from '@ey-xd/motif-wc-react';
import { ClientDropdown } from './components/ClientDropdown';
import { ErrorBoundary } from '@/error/ErrorBoundary';
import { UserMenu } from './components/UserMenu';
import {
  HamburgerButton,
  WorkspacesButton,
  HelpButton,
  RefreshButton,
  GroupButton,
  OrchestratorButton,
  ChatBotButton,
} from './components/HeaderButtons';
import { HeaderUtilityPanels } from './components/HeaderUtilityPanels';
import { eventsModule } from '@/events/events-module';
import { useHeaderIconVisibility } from './use-header-icon-visibility';

/**
 * Top-level header. Replaces the entire legacy `raptor-shell-header`
 * Angular sub-app:
 *
 *   left slot   : hamburger (toggles side-nav)
 *   brand       : app name + logo link
 *   middle slot : client picker (with switch-confirmation modal) · refresh
 *   right slot  : orchestrator · people · apps · help · chat · avatar
 *
 * Icons used: `@ey-xd/motif-icon/Icons/Iconoir/*` — same set the Motif
 * design system ships, so we don't introduce another icon dependency.
 * MyTeams, orchestrator, chatbot are deferred to M3+.
 */
export function ShellHeader({ navOpen = true }: { navOpen?: boolean }) {
  const headerIcons = useHeaderIconVisibility();

  // `ElementRef<typeof MotifHeader>` is the custom-element type the MotifHeader
  // ref points to (it extends HTMLElement), so the `ref` assignment type-checks
  // while the effect below can still use HTMLElement members (shadowRoot, style).
  const headerRef = useRef<ElementRef<typeof MotifHeader>>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    // motif-header's shadow DOM sets overflow:hidden on internal containers,
    // clipping the motif-select dropdown panel. Once the component hydrates,
    // walk its shadow tree and force overflow:visible on clipping ancestors.
    const forceOverflow = () => {
      const shadow = el.shadowRoot;
      if (!shadow) return;
      shadow.querySelectorAll<HTMLElement>('*').forEach((node: HTMLElement) => {
        const style = getComputedStyle(node);
        if (
          style.overflow === 'hidden' ||
          style.overflowY === 'hidden' ||
          style.overflowX === 'hidden'
        ) {
          node.style.overflow = 'visible';
        }
      });
    };

    // Disable Motif header's mobile collapse. The Angular shell's
    // ey-global-header has min-width:450px and no mobile hamburger; it simply
    // compresses at narrow widths. Motif hides middleComponents and shows a
    // mobile hamburger at <36em (~576px). Inject a <style> into shadow DOM to
    // always show the desktop layout and never show the mobile elements.
    const MOBILE_OVERRIDE_ID = 'shell-mobile-override';
    const injectMobileOverride = () => {
      const shadow = el.shadowRoot;
      if (!shadow || shadow.getElementById(MOBILE_OVERRIDE_ID)) return;
      const style = document.createElement('style');
      style.id = MOBILE_OVERRIDE_ID;
      style.textContent = `
        .mobile-menu-toggle,
        .expanded-menu,
        .close-button {
          display: none !important;
        }
        .motif-header ::slotted([slot=middleComponents]) {
          display: flex !important;
        }
        .motif-header ::slotted([slot=iconsContainer]) {
          display: flex !important;
          flex-shrink: 0 !important;
        }
        .motif-header ::slotted([slot=leftComponents]) {
          flex-shrink: 0 !important;
        }
        .motif-header {
          min-width: 450px !important;
          flex-wrap: nowrap !important;
        }
        .motif-header .motif-header-top {
          flex-wrap: nowrap !important;
        }
        .motif-header .motif-header-left {
          flex-shrink: 0 !important;
          overflow: visible !important;
        }
        .motif-header .motif-header-right {
          flex-shrink: 0 !important;
          overflow: visible !important;
        }
      `;
      shadow.appendChild(style);
    };

    // Run after hydration and on any attribute change (component may re-render)
    const timer = setTimeout(() => {
      forceOverflow();
      injectMobileOverride();
    }, 100);
    const observer = new MutationObserver(() => {
      forceOverflow();
      injectMobileOverride();
    });
    observer.observe(el, { childList: true, subtree: true, attributes: true });
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <MotifHeader ref={headerRef} appHeaderName="" appHeaderTitle="RAPToR Portal" linkPath="/">
        <div
          slot="leftComponents"
          // Angular's `.left` group uses per-element margins (CLD `_theme.scss`:
          // logo `margin: 10 16 10 20`, headline `margin-right: 20`) instead of
          // a uniform gap — that produces the airy spacing between hamburger,
          // logo and title that a single 8px `gap` was flattening. No `gap`
          // here; each child owns its own horizontal margin.
          style={{
            display: 'flex',
            alignItems: 'center',
            color: '#ffffff',
            height: '100%',
          }}
        >
          <HamburgerButton
            open={navOpen}
            onClick={() => eventsModule.emit('shell:toggle-sidenav')}
          />
          <a
            href="/"
            aria-label="RAPToR Portal home"
            // CLD `.logo { margin: 10px 16px 10px 20px }` — 20px left of the
            // logo cell (from the hamburger), 16px right (to the headline).
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: '100%',
              marginLeft: 20,
              marginRight: 16,
            }}
          >
            <img
              src="/ey-logo-white.svg"
              alt="EY"
              // CLD `.logo-container { height: 26px; width: 26px; margin-top: 1px }`
              // sitting inside `.logo { margin: 10px 16px 10px 20px }` in a 60px
              // header — that puts the logo's optical center ~6px ABOVE the
              // header's dead center. Flex `align-items: center` on the parent
              // parks it dead-center, so pull it up 6px to match CLD.
              style={{ height: 26, width: 'auto', display: 'block', marginTop: '-6px' }}
            />
          </a>
          <div
            // CLD `.headline { margin-right: 20px }` — explicit gutter to the
            // client picker, matching Angular's airier feel.
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.2,
              marginRight: 20,
            }}
          >
            <span
              style={{
                fontWeight: 500,
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'EYInterstate-Regular, sans-serif',
              }}
            >
              Global Tax Platform
            </span>
            <span
              // CLD `.headline-subtitle { color: $color-ey-gray-2 = #c4c4cd }`
              // — muted grey subtitle, not pure white, per the design spec.
              style={{
                color: '#c4c4cd',
                fontSize: '10px',
                fontFamily: 'EYInterstate-Light, sans-serif',
              }}
            >
              <i>Powered by Microsoft </i>&reg;
            </span>
          </div>
        </div>
        <div
          slot="middleComponents"
          style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#ffffff' }}
        >
          <ErrorBoundary
            context="client_picker"
            fallback={
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  minWidth: 270,
                  height: 32,
                  borderRadius: 4,
                  border: '1px solid #6c6c74',
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Client picker unavailable. Reload
              </button>
            }
          >
            <ClientDropdown />
          </ErrorBoundary>
          <RefreshButton />
        </div>
        {/*
         * Icon-only action buttons go into `iconsContainer` — that slot stays
         * visible at narrow viewports, whereas `rightComponents` collapses
         * behind the mobile expanded-menu toggle. Heavier content (UserMenu
         * with dropdown) remains in `rightComponents`.
         */}
        <div
          slot="iconsContainer"
          style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: '#ffffff' }}
        >
          {headerIcons.showOrchestrator && <OrchestratorButton />}
          {headerIcons.showGroups && <GroupButton />}
          {headerIcons.showApps && <WorkspacesButton />}
          {headerIcons.showHelp && <HelpButton />}
          {(headerIcons.showChatBot || headerIcons.showEyqfs) && (
            <ChatBotButton chatbotType={headerIcons.chatbotType} />
          )}
          <UserMenu />
        </div>
      </MotifHeader>
      <HeaderUtilityPanels />
    </>
  );
}
